import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { downloadCSV, inr, Kpi, EmptyRow } from "./utils";

export default function WastageReport() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [view, setView] = useState("operator");

  const load = () => {
    setBusy(true);
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    api.get("/reports/wastage", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rows = data ? (view === "operator" ? data.by_operator : data.by_machine) : [];
  const totals = data?.totals || {};

  const exportCSV = () => downloadCSV(
    `wastage-by-${view}`,
    ["Name", "Code", view === "machine" ? "Type" : "", "Output Qty", "Waste Qty", "Sessions", "Waste %"].filter(Boolean),
    rows.map((r) => {
      const base = [r.name, r.code || ""];
      if (view === "machine") base.push(r.type || "");
      return [...base, r.output_qty, r.waste_qty, r.sessions, r.waste_pct];
    })
  );

  return (
    <div>
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-caps block mb-1">From date</label>
          <Input data-testid="wast-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm w-40" />
        </div>
        <div>
          <label className="label-caps block mb-1">To date</label>
          <Input data-testid="wast-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm w-40" />
        </div>
        <div>
          <label className="label-caps block mb-1">Group by</label>
          <Select value={view} onValueChange={setView}>
            <SelectTrigger data-testid="wast-view" className="rounded-sm w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="operator">Operator</SelectItem>
              <SelectItem value="machine">Machine</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="wast-refresh" onClick={load} disabled={busy} variant="outline" className="rounded-sm">
          {busy ? "Loading…" : "Apply"}
        </Button>
        <Button data-testid="wast-export" onClick={exportCSV} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          Download .csv
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <Kpi label="Total output" value={inr(totals.total_output || 0)} tone="ok" />
        <Kpi label="Total waste" value={inr(totals.total_waste || 0)} tone="warn" />
        <Kpi label="Overall waste %" value={`${totals.overall_waste_pct || 0}%`}
          tone={totals.overall_waste_pct > 5 ? "danger" : totals.overall_waste_pct > 2 ? "warn" : "ok"} />
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="wast-table">
          <thead className="bg-neutral-50 label-caps">
            <tr>
              <th className="text-left px-4 py-2.5">{view === "operator" ? "Operator" : "Machine"}</th>
              <th className="text-left px-4 py-2.5">Code</th>
              {view === "machine" && <th className="text-left px-4 py-2.5">Type</th>}
              <th className="text-right px-4 py-2.5">Sessions</th>
              <th className="text-right px-4 py-2.5">Output</th>
              <th className="text-right px-4 py-2.5">Waste</th>
              <th className="text-right px-4 py-2.5">Waste %</th>
              <th className="text-left px-4 py-2.5 w-56">Bar</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow cols={view === "machine" ? 8 : 7} msg="No production logs found in this range." />}
            {rows.map((r) => {
              const tone = r.waste_pct >= 5 ? "bg-rose-500" : r.waste_pct >= 2 ? "bg-amber-500" : "bg-emerald-500";
              const barColor = r.waste_pct >= 5 ? "text-rose-700" : r.waste_pct >= 2 ? "text-amber-700" : "text-emerald-700";
              return (
                <tr key={r.operator_id || r.machine_id} className="border-t border-neutral-100 data-row" data-testid={`wast-row-${r.code || r.name}`}>
                  <td className="px-4 py-2.5 font-medium">{r.name}</td>
                  <td className="px-4 py-2.5 font-mono-num text-xs text-neutral-600">{r.code || "—"}</td>
                  {view === "machine" && <td className="px-4 py-2.5 text-xs text-neutral-600">{r.type || "—"}</td>}
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.sessions}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num text-emerald-700">{inr(r.output_qty)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num text-amber-700">{inr(r.waste_qty)}</td>
                  <td className={`px-4 py-2.5 text-right font-mono-num font-semibold ${barColor}`}>{r.waste_pct}%</td>
                  <td className="px-4 py-2.5">
                    <div className="h-2 bg-neutral-100">
                      <div className={`h-full ${tone}`} style={{ width: `${Math.min(100, r.waste_pct * 4)}%` }}></div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-neutral-500">Sorted by highest waste % first. Bar scaled at 25% waste = full width.</p>
    </div>
  );
}


